/**
 * WS4 Handshake Board info-panel copy — verbatim port of
 * `XAES_CONTENT.ws4.handshake` from `_legacy/xaes-content.js`
 * lines 72-92 (the `handshake` node).
 *
 * Vanilla renders this copy into the left rail of the handshake
 * whiteboard modal (`#handshake-panel-body`, see
 * `_legacy/wd.html` lines 975-978, populated by
 * `renderHandshakePanel()` in `_legacy/xaes-content.js` lines 696-743).
 *
 * Strings are copied character-for-character from vanilla — do not
 * paraphrase. The `color` field on a prompt is the swatch the vanilla
 * code used to seed a sticky note when the pill was clicked; we keep
 * the raw value (some are hex, some are CSS custom-property refs) so
 * the React port can either resolve `var(--accent-…)` at runtime or
 * use the hex directly on the canvas.
 */

export type HandshakePromptAccent =
  | "orange"
  | "blue"
  | "green"
  | "melon"
  | "cyan";

export type HandshakePrompt = {
  /** Short pill label, e.g. "+ Trust threshold". */
  label: string;
  /** Long-form expansion used as the sticky-note text in vanilla. */
  text: string;
  /** Seed colour for the sticky / canvas stroke. Hex or `var(--…)`. */
  color: string;
  /** DXC accent slot driving the pill's chrome (bg/border/text). */
  accentVar: HandshakePromptAccent;
};

export type HandshakeContent = {
  /** Workshop objective — uppercase title above the description. */
  objective: string;
  /** One-paragraph framing for the activity. */
  description: string;
  /** Bulleted prompts to seed group discussion. */
  keyQuestions: string[];
  /** Click-to-seed prompt pills shown below the questions. */
  prompts: HandshakePrompt[];
  /** Footer hint nudging the user toward the canvas tools. */
  boardHint: string;
};

export const HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Define the Human-Agent Handshake",
  description:
    "Establish clear protocols for how humans and AI agents transfer control, share context, and signal intent during collaborative workflows.",
  keyQuestions: [
    "When does the human hand off to the agent?",
    "What context must be passed at handoff?",
    "How does the agent signal completion or escalation?",
    "What are the failure modes and recovery paths?",
  ],
  prompts: [
    {
      label: "+ Trust threshold",
      text: "Trust threshold: what level of confidence before agent acts?",
      color: "#ffae41",
      accentVar: "orange",
    },
    {
      label: "+ Handoff trigger",
      text: "Handoff trigger: what event initiates the handshake?",
      color: "#4995FF",
      accentVar: "blue",
    },
    {
      label: "+ Context package",
      text: "Context package: what information must be transferred?",
      color: "#41C889",
      accentVar: "green",
    },
    {
      label: "+ Escalation path",
      text: "Escalation path: when and how does agent return control?",
      color: "var(--accent-melon)",
      accentVar: "melon",
    },
    {
      label: "+ Audit trail",
      text: "Audit trail: how is the handshake logged and reviewed?",
      color: "#A1E6FF",
      accentVar: "cyan",
    },
  ],
  boardHint:
    "Use the board to capture ideas, map flows, and define your team's handshake protocol. Sticky notes, freehand drawing, and text labels are all available.",
};

const WS1_HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Align on Executive Sponsorship & Success",
  description:
    "Capture what a win looks like to the executives in the room, who owns it, and what could quietly kill sponsorship before the program scales.",
  keyQuestions: [
    "What does success look like in 90 days, and who is accountable for it?",
    "Which executive is the visible sponsor when priorities collide?",
    "What budget or resourcing risk could stall this before it starts?",
    "How will we know early if sponsorship is slipping?",
  ],
  prompts: [
    { label: "+ Success metric", text: "Success metric: what number or outcome proves this worked?", color: "#ffae41", accentVar: "orange" },
    { label: "+ Sponsor", text: "Sponsor: who is the single accountable executive?", color: "#4995FF", accentVar: "blue" },
    { label: "+ Risk to sponsorship", text: "Risk to sponsorship: what could make leadership disengage?", color: "#41C889", accentVar: "green" },
    { label: "+ Early signal", text: "Early signal: how will we notice momentum stalling?", color: "var(--accent-melon)", accentVar: "melon" },
  ],
  boardHint:
    "Use the board to map sponsors, success metrics and risks. Sticky notes, freehand drawing, and text labels are all available.",
};

const WS2_HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Select & Baseline the Process",
  description:
    "Choose the process to take forward and pin down what 'today' actually looks like — the real steps, the real exceptions, the real numbers.",
  keyQuestions: [
    "Which process gives the clearest, most credible early win?",
    "Who lives in this process day-to-day and can validate the baseline?",
    "What data do we trust, and what are we guessing at?",
    "What exceptions or edge cases does the happy path hide?",
  ],
  prompts: [
    { label: "+ Candidate process", text: "Candidate process: name it and why it's a strong first pick.", color: "#ffae41", accentVar: "orange" },
    { label: "+ Baseline metric", text: "Baseline metric: volume, cycle time, error rate — pick what's measurable.", color: "#4995FF", accentVar: "blue" },
    { label: "+ Process owner", text: "Process owner: who validates this baseline is accurate?", color: "#41C889", accentVar: "green" },
    { label: "+ Hidden exception", text: "Hidden exception: what breaks the happy path in practice?", color: "var(--accent-melon)", accentVar: "melon" },
  ],
  boardHint:
    "Use the board to sketch the current process flow and mark where the baseline data is solid versus shaky.",
};

const WS3_HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Design the AI-Enhanced To-Be Process",
  description:
    "Redraw the process with AI in the loop — what the agent takes on, what stays human, and where control changes hands.",
  keyQuestions: [
    "Which steps does the agent take on end-to-end, and which stay human?",
    "Where does the redesigned flow still need a human judgement call?",
    "What breaks if input data or volume looks nothing like today?",
    "How do we design for the exception, not just the happy path?",
  ],
  prompts: [
    { label: "+ Agent-owned step", text: "Agent-owned step: what does the agent fully handle in the new flow?", color: "#ffae41", accentVar: "orange" },
    { label: "+ Human checkpoint", text: "Human checkpoint: where must a person still decide or approve?", color: "#4995FF", accentVar: "blue" },
    { label: "+ Failure path", text: "Failure path: what happens when the agent gets it wrong?", color: "#41C889", accentVar: "green" },
    { label: "+ Data dependency", text: "Data dependency: what input quality does this design assume?", color: "var(--accent-melon)", accentVar: "melon" },
  ],
  boardHint:
    "Use the board to map the to-be flow, mark agent-owned steps versus human checkpoints, and flag open design questions.",
};

const WS5_HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Define Governance, Risk & Safety Guardrails",
  description:
    "Agree the guardrails an agent must operate inside — who is accountable, what gets logged, and how incidents are escalated.",
  keyQuestions: [
    "What decisions is the agent never allowed to make unsupervised?",
    "Who is accountable when an agent's action turns out to be wrong?",
    "What must be logged so an audit can reconstruct any decision?",
    "What is the escalation path when something goes wrong in production?",
  ],
  prompts: [
    { label: "+ Hard guardrail", text: "Hard guardrail: what action must always require human sign-off?", color: "#ffae41", accentVar: "orange" },
    { label: "+ Accountable owner", text: "Accountable owner: who answers for this agent's decisions?", color: "#4995FF", accentVar: "blue" },
    { label: "+ Audit trail", text: "Audit trail: what gets logged, and for how long?", color: "#41C889", accentVar: "green" },
    { label: "+ Escalation trigger", text: "Escalation trigger: what condition pages a human immediately?", color: "var(--accent-melon)", accentVar: "melon" },
  ],
  boardHint:
    "Use the board to map guardrails, ownership, and escalation paths. Sticky notes work well for grouping risks by severity.",
};

const WS6_HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Plan the Phase 0 Blueprint Demo",
  description:
    "Storyboard the demo that proves the blueprint works end-to-end — what it shows, what it deliberately skips, and what could go wrong live.",
  keyQuestions: [
    "What single end-to-end scenario proves the blueprint holds together?",
    "What are we deliberately not showing yet, and why?",
    "What's our fallback if something breaks live in front of stakeholders?",
    "What question do we most want the audience to ask afterward?",
  ],
  prompts: [
    { label: "+ Demo scenario", text: "Demo scenario: the one flow that best proves the blueprint.", color: "#ffae41", accentVar: "orange" },
    { label: "+ Known gap", text: "Known gap: what's intentionally out of scope for this demo?", color: "#4995FF", accentVar: "blue" },
    { label: "+ Fallback plan", text: "Fallback plan: what do we do if the live demo fails?", color: "#41C889", accentVar: "green" },
    { label: "+ Audience takeaway", text: "Audience takeaway: what should stakeholders walk away believing?", color: "var(--accent-melon)", accentVar: "melon" },
  ],
  boardHint:
    "Use the board to storyboard the demo flow step by step and flag any risky moments to rehearse.",
};

const WS7_HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Shape the Executive Showcase",
  description:
    "Decide what story convinces the widest set of executives that this program deserves continued investment.",
  keyQuestions: [
    "Which executives in the room still need to be won over, and why?",
    "What proof point lands hardest with a skeptical budget holder?",
    "What honest limitation should we name before someone else does?",
    "What's the single ask we want leadership to say yes to?",
  ],
  prompts: [
    { label: "+ Proof point", text: "Proof point: the strongest evidence this program is working.", color: "#ffae41", accentVar: "orange" },
    { label: "+ Skeptic to win", text: "Skeptic to win: which stakeholder is hardest to convince?", color: "#4995FF", accentVar: "blue" },
    { label: "+ Honest limitation", text: "Honest limitation: what should we name before it's found for us?", color: "#41C889", accentVar: "green" },
    { label: "+ The ask", text: "The ask: what decision do we want leadership to make today?", color: "var(--accent-melon)", accentVar: "melon" },
  ],
  boardHint:
    "Use the board to map proof points against each stakeholder and draft the single ask we're taking into the showcase.",
};

const WS8_HANDSHAKE_CONTENT: HandshakeContent = {
  objective: "Lock the Flight Plan",
  description:
    "Turn momentum into a named, sequenced plan for the next phase — owners, dependencies, and the date each milestone is due.",
  keyQuestions: [
    "What are the first three milestones, in order, after this program?",
    "Who owns each milestone by name, not just by team?",
    "What dependency could delay the whole plan if it slips?",
    "How and when do we check progress against this plan?",
  ],
  prompts: [
    { label: "+ Next milestone", text: "Next milestone: the very next thing that has to happen.", color: "#ffae41", accentVar: "orange" },
    { label: "+ Named owner", text: "Named owner: who is personally accountable for this milestone?", color: "#4995FF", accentVar: "blue" },
    { label: "+ Dependency risk", text: "Dependency risk: what could this milestone get stuck waiting on?", color: "#41C889", accentVar: "green" },
    { label: "+ Check-in cadence", text: "Check-in cadence: when do we next review progress together?", color: "var(--accent-melon)", accentVar: "melon" },
  ],
  boardHint:
    "Use the board to sequence milestones, assign owners, and flag dependencies that could delay the plan.",
};

/**
 * RT-CANVAS — per-workshop handshake content. Every workshop now has an
 * authored whiteboard activity, themed to that workshop's own topic, so
 * the Handshake board is usable from any workshop reached via the
 * WorkshopNavigator. WS4 keeps its original content (back-compat).
 */
const HANDSHAKE_BY_WORKSHOP: Partial<Record<string, HandshakeContent>> = {
  WS1: WS1_HANDSHAKE_CONTENT,
  WS2: WS2_HANDSHAKE_CONTENT,
  WS3: WS3_HANDSHAKE_CONTENT,
  WS4: HANDSHAKE_CONTENT,
  WS5: WS5_HANDSHAKE_CONTENT,
  WS6: WS6_HANDSHAKE_CONTENT,
  WS7: WS7_HANDSHAKE_CONTENT,
  WS8: WS8_HANDSHAKE_CONTENT,
};

/**
 * Handshake info-panel content for a workshop, or `null` when none is
 * authored (the board renders a per-workshop empty state). WS4 keeps its
 * original content (back-compat). RT-CANVAS getter — read this instead of
 * the `HANDSHAKE_CONTENT` constant directly.
 */
export function getHandshakeContent(workshopId: string): HandshakeContent | null {
  return HANDSHAKE_BY_WORKSHOP[workshopId] ?? null;
}
